import {
  PSL_COMMIT,
  PSL_EXACT_RULES,
  PSL_EXCEPTION_RULES,
  PSL_FETCHED_AT,
  PSL_VERSION,
  PSL_WILDCARD_RULES,
} from './public-suffix-data.js';

/**
 * The Public Suffix List algorithm (OI-14).
 *
 * A *public suffix* is the part of a hostname under which anybody may register
 * a name — `com`, `co.uk`, `github.io`. The *registrable domain* is one label
 * above it, and that is what per-domain rate limiting must key on: without the
 * list, `bbc.co.uk` and `theguardian.co.uk` look like one site called `co.uk`
 * and every UK publisher shares a token bucket.
 *
 * ## Why the list is not a set of strings
 *
 * The obvious shape — "is this suffix in a Set?" — cannot express two of the
 * list's three rule kinds, and both exist in the real data:
 *
 * - **Wildcards.** `*.ck` makes `anything.ck` a public suffix, so `foo.bar.ck`
 *   registers at `foo.bar.ck` rather than at `bar.ck`. There are 289 of these.
 * - **Exceptions.** `!www.ck` cancels the wildcard for one name, so the public
 *   suffix there is `ck` after all. There are 8, and they exist precisely
 *   because the wildcard got it wrong somewhere.
 *
 * The algorithm is the one publicsuffix.org specifies: find the **longest**
 * matching rule, with exceptions taking priority, then take one more label.
 *
 * ## Where it fails, and in which direction
 *
 * A host with no matching rule falls back to "the last label is the suffix",
 * which is the spec's prevailing `*` rule. That means a brand-new TLD the
 * committed list has not seen yet is treated as an ordinary one — a registrable
 * domain one label too short, so two sites *might* share a bucket and we are
 * too polite. Being too polite is a latency cost; being too aggressive is
 * somebody else's server.
 */

/** Built once at module load — the data is three newline-joined strings. */
const EXACT = new Set(PSL_EXACT_RULES.split('\n'));
const WILDCARD = new Set(PSL_WILDCARD_RULES.split('\n'));
const EXCEPTION = new Set(PSL_EXCEPTION_RULES.split('\n'));

/** Which list this is, for the diagnostics endpoint and the staleness test. */
export const publicSuffixListVersion = {
  version: PSL_VERSION,
  commit: PSL_COMMIT,
  fetchedAt: PSL_FETCHED_AT,
  rules: EXACT.size + WILDCARD.size + EXCEPTION.size,
} as const;

/**
 * The public suffix of a hostname, by the published algorithm.
 *
 * Returns the empty string for an empty host. The host is expected to be
 * lower-cased and free of a trailing dot; `registrableDomain` handles both.
 */
export function publicSuffix(host: string): string {
  const labels = host.split('.');

  for (let index = 0; index < labels.length; index += 1) {
    const candidate = labels.slice(index).join('.');

    /*
     * Exceptions first, at every level. The spec says an exception rule wins
     * over any other match, and an exception is always longer than the
     * wildcard it cancels — so checking it at the same level, before the other
     * two, is equivalent to checking all exceptions first.
     */
    if (EXCEPTION.has(candidate)) {
      return labels.slice(index + 1).join('.');
    }

    if (EXACT.has(candidate)) {
      return candidate;
    }

    // `*.parent` makes *this* level a suffix, whatever the label is.
    if (index + 1 < labels.length && WILDCARD.has(labels.slice(index + 1).join('.'))) {
      return candidate;
    }
  }

  // The prevailing rule when nothing matches is `*`: the TLD.
  return labels[labels.length - 1] ?? '';
}

/**
 * The registrable domain — the public suffix plus one label.
 *
 * Returns `undefined` when the host *is* a public suffix and nothing can be
 * registered at it: `co.uk` on its own, or a bare TLD. Callers decide what to
 * do with that; the rate limiter falls back to the host itself, which is the
 * conservative direction.
 */
export function registrableDomain(host: string): string | undefined {
  const normalised = host.toLowerCase().replace(/\.+$/, '');

  if (normalised === '' || normalised.startsWith('.')) {
    return undefined;
  }

  /*
   * An IP address has no registrable domain, and running the algorithm on one
   * would return `1` for `10.0.0.1` — a bucket shared by every address ending
   * in that digit.
   */
  if (/^\d+(\.\d+)*$/.test(normalised) || normalised.includes(':')) {
    return undefined;
  }

  const suffix = publicSuffix(normalised);

  if (suffix === normalised) {
    return undefined;
  }

  const suffixLabels = suffix === '' ? 0 : suffix.split('.').length;
  const labels = normalised.split('.');

  if (labels.length <= suffixLabels) {
    return undefined;
  }

  return labels.slice(labels.length - suffixLabels - 1).join('.');
}
